import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Periods } from 'components/table/table-filters-components/date-picker/date-picker';
import { DatePickerNames } from 'constants/date-picker-ids';
import dayjs from 'dayjs';

export interface DatePicker {
  activityStartDate?: string;
  activityEndDate?: string;
  activityPeriod: string;
  calendarTab: number;
}

export type DatePickerStore = Record<string, DatePicker>;

const initialDatePickerState: DatePicker = {
  activityPeriod: Periods[2],
  activityStartDate: dayjs(new Date())
    .subtract(7, 'days')
    .format('YYYY-MM-DDTHH:mm:ss'),
  activityEndDate: dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
  calendarTab: 0,
};

const initialDatePickerStore: DatePickerStore = {
  [DatePickerNames.SECRETS]: initialDatePickerState,
  [DatePickerNames.ACCESS_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.ASSETS_ACTIVITY]: initialDatePickerState,
  [DatePickerNames.ASSETS_PIPELINE]: initialDatePickerState,
  [DatePickerNames.ASSETS_PULL_REQUESTS]: initialDatePickerState,
  [DatePickerNames.IDENTITIES_ACTIVITY]: initialDatePickerState,
  [DatePickerNames.IDENTITIES_AUDIT_TRAIL]: initialDatePickerState,
  [DatePickerNames.IDENTITIES_PIPELINE_ACTIVITY]: initialDatePickerState,
  [DatePickerNames.PRODUCTIVITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.ORGANIZATIONAL_TEAMS_PRODUCTIVITY]: initialDatePickerState,
  [DatePickerNames.IAC_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.SAST_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.DAST_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.SECRET_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.CSPM_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.CONTAINER_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.REGISTRY_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.OSS_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.ALL_VULNERABILITY_INSIGHTS]: initialDatePickerState,
  [DatePickerNames.VULNERABILITY_OVERVIEW]: initialDatePickerState,
  [DatePickerNames.CI_CD_INSIGHTS]: initialDatePickerState,
};

const DatePickerSlice = createSlice({
  name: 'date',
  initialState: initialDatePickerStore,
  reducers: {
    resetDatePickerState: () => initialDatePickerStore,
    setActivityStartDate: (
      state: DatePickerStore,
      action: PayloadAction<{
        id: string;
        activityStartDate: string | undefined;
      }>,
    ) => {
      const { id, activityStartDate } = action.payload;
      state[id].activityStartDate = activityStartDate;
    },
    setActivityEndDate: (
      state: DatePickerStore,
      action: PayloadAction<{
        id: string;
        activityEndDate: string | undefined;
      }>,
    ) => {
      const { id, activityEndDate } = action.payload;
      state[id].activityEndDate = activityEndDate;
    },
    setActivityPeriod: (
      state: DatePickerStore,
      action: PayloadAction<{ id: string; activityPeriod: string }>,
    ) => {
      const { id, activityPeriod } = action.payload;
      state[id].activityPeriod = activityPeriod;
    },
    setCalendarTab: (
      state: DatePickerStore,
      action: PayloadAction<{ id: string; calendarTab: number }>,
    ) => {
      const { id, calendarTab } = action.payload;
      state[id].calendarTab = calendarTab;
    },
  },
});

export const {
  setCalendarTab,
  setActivityPeriod,
  setActivityEndDate,
  setActivityStartDate,
  resetDatePickerState,
} = DatePickerSlice.actions;

export const datePickerReducer = DatePickerSlice.reducer;
